import React, { useEffect, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  TrackToggle,
  useTracks,
  useConnectionState,
  useLocalParticipant,
} from "@livekit/components-react";
import { Track, ConnectionState } from "livekit-client";
import "@livekit/components-styles";
import { Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function Stage({ isHost, poster, onGoLive }) {
  const state = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const [starting, setStarting] = useState(false);
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: !isHost }
  );
  const live = tracks.length > 0;

  const startBroadcast = async () => {
    setStarting(true);
    try {
      await localParticipant.setCameraEnabled(true);
      await localParticipant.setMicrophoneEnabled(true);
      onGoLive?.();
    } catch (e) {
      toast.error("Couldn't access your camera. Please allow camera & mic permissions.");
    } finally { setStarting(false); }
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "#000" }} data-testid="livekit-stage">
      {live ? (
        tracks.map((t) => (
          <VideoTrack
            key={`${t.participant.identity}-${t.source}`}
            trackRef={t}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ))
      ) : (
        <>
          {poster && <img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.4 }} />}
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#fff", textAlign: "center", padding: 20 }}>
            {state === ConnectionState.Connecting ? (
              <div data-testid="livekit-connecting"><Loader2 size={26} className="spin" /><div style={{ marginTop: 8, fontWeight: 700 }}>Connecting…</div></div>
            ) : isHost ? (
              <div data-testid="livekit-host-idle">
                <Radio size={26} />
                <div style={{ marginTop: 8, fontWeight: 800 }}>Ready to go live?</div>
                <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 14 }}>Start your camera to begin broadcasting to viewers.</div>
                <button onClick={startBroadcast} disabled={starting} data-testid="go-live-camera"
                  className="ss-btn ss-btn-live" style={{ margin: "0 auto" }}>
                  <Radio size={16} /> {starting ? "Starting…" : "Go Live"}
                </button>
              </div>
            ) : (
              <div data-testid="livekit-waiting">
                <Radio size={26} />
                <div style={{ marginTop: 8, fontWeight: 800 }}>Waiting for the host…</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>The live feed will appear here the moment they go on air.</div>
              </div>
            )}
          </div>
        </>
      )}

      <RoomAudioRenderer />

      {isHost && (
        <div className="lk-host-controls" data-testid="livekit-host-controls"
          style={{ position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 10, zIndex: 5 }}>
          <TrackToggle source={Track.Source.Camera} data-testid="lk-camera-toggle" />
          <TrackToggle source={Track.Source.Microphone} data-testid="lk-mic-toggle" />
        </div>
      )}
    </div>
  );
}

export default function LiveVideoPlayer({ showId, isHost, poster, onGoLive }) {
  const [conn, setConn] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    api.post(`/shows/${showId}/livekit-token`, {})
      .then((d) => { if (alive) setConn({ serverUrl: d.server_url, token: d.token }); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [showId]);

  if (failed) {
    return poster
      ? <img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      : <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#fff", background: "#000" }}>Live video unavailable</div>;
  }

  if (!conn) {
    return (
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#fff", background: "#000" }} data-testid="livekit-loading">
        <Loader2 size={26} className="spin" />
      </div>
    );
  }

  return (
    <LiveKitRoom
      serverUrl={conn.serverUrl}
      token={conn.token}
      connect
      video={false}
      audio={false}
      style={{ position: "absolute", inset: 0 }}
      onError={() => setFailed(true)}
      data-lk-theme="default"
    >
      <Stage isHost={isHost} poster={poster} onGoLive={onGoLive} />
    </LiveKitRoom>
  );
}
